import { useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { Formik, Form } from 'formik'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import { Typography, useMediaQuery } from '@mui/material'

import { useTheme } from '@mui/system'

import { AppVersion, AuthFormWrapper, HelperWrapper, Loader, Recaptcha } from 'src/components'
import {
  useSignup,
  useSignupAccountPreCreateCheck,
  useSignUpSettings,
  useVerification,
} from 'src/hooks'
import { IUserSignupDto, IUserSignupForm } from 'src/types'

import { signUpSchema } from 'src/common/validations'
import { SPACES, ROUTES, AppEvents } from 'src/constants'

import { useStyles } from '../styles'
import { SignUpFormV2 } from '../v2/SignUpFormV2'
import { AgreementStep } from '../v2/AgreementStep/AgreementStep'
import { getEncodedQueryParam } from 'src/common/utils'
import { useSnackbar } from 'notistack'

const Signup = () => {
  const { verifyAsync, captchaRef, verificationResp } = useVerification()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { mutateAsync: signup } = useSignup()
  const { mutateAsync: checkExistingAccount, data: existingAccountResp } =
    useSignupAccountPreCreateCheck()
  const theme = useTheme()
  const [step, setStep] = useState(0)

  const { enqueueSnackbar } = useSnackbar()

  const email = getEncodedQueryParam('email')
  const withoutAccount = searchParams.get('without_account')

  const { data: settings, isLoading: isSettingsLoading } = useSignUpSettings()
  // const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  // Styling
  const classes = useStyles()
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop'))

  const handleSignup = async (values: IUserSignupForm) => {
    if (!verificationResp) {
      const isVerified = await verifyAsync()

      if (!isVerified) {
        enqueueSnackbar('Verification failed. Please try again', { variant: 'error' })

        return
      }
    }

    if (!values.buisness) {
      return await signup({
        email: values.email,
        password: values.password,
        agreement: values.agreement,
        customer: !values.buisness,
        journeyRedirect: (location?.state?.journeyRedirect as string) || '',
      })
    }

    if (withoutAccount) {
      return await signup({
        email: values.email,
        password: values.password,
        agreement: values.agreement,
        journeyRedirect: (location?.state?.journeyRedirect as string) || '',
        customer: false,
      })
    }

    if (values.withExistingAccount) {
      return await signup({
        agreement: values.agreement,
        password: values.password,
        email: values.email,
        accountId: existingAccountResp?.id,
        journeyRedirect: (location?.state?.journeyRedirect as string) || '',
        customer: false,
      })
    }

    if (!existingAccountResp) {
      const resp = await checkExistingAccount({
        account_holder: values.account_holder!,
        website: values.account_website || null,
      })
      if (resp) return
    }

    const dto: IUserSignupDto = {
      agreement: values.agreement,
      password: values.password,
      email: values.email,
      account_holder: values.account_holder,
      account_website: values.account_website,
      withoutExistCheck: true,
      journeyRedirect: (location?.state?.journeyRedirect as string) || '',
      customer: false,
    }

    await signup(dto)
  }

  return (
    <Grid
      item
      xs={isLaptop ? 12 : 6}
      px={isLaptop ? SPACES.xxxl : 0}
      className={classes.contentWrapper}
    >
      <Grid
        display={isLaptop ? 'none' : 'flex'}
        justifyContent="flex-end"
        className={classes.signUpContainer}
      >
        <Typography className={classes.textSignUp} alignItems={'center'}>
          <HelperWrapper t="sign-in-email-opposite-text" />{' '}
          <Link style={{ textDecoration: 'none' }} to={ROUTES.LOGIN}>
            <Button className={classes.buttonSignUp}>
              <HelperWrapper t="login-link-text" />
            </Button>
          </Link>
        </Typography>
      </Grid>
      <AuthFormWrapper>
        <Grid container direction="column" justifyContent="center" alignItems="center">
          <Grid>
            <>
              {(!settings || isSettingsLoading) && <Loader />}

              {settings && settings.sign_up_enabled && (
                <Formik
                  initialValues={{
                    email: email ? email : '',
                    password: '',
                    account_holder: '',
                    agreement: false,
                    withExistingAccount: false,
                    account_website: '',
                    buisness: false,
                  }}
                  autoComplete="off"
                  onSubmit={handleSignup}
                  validationSchema={signUpSchema(!!withoutAccount)}
                >
                  <>
                    <Form className={classes.form}>
                      {/* {step === 0 && <SignupInitialStep setStep={setStep} />} */}
                      {step === 0 && (
                        <SignUpFormV2
                          setStep={setStep}
                          captcha={{ verifyAsync: verifyAsync, verificationResp: verificationResp }}
                        />
                      )}
                      {step === 1 && (
                        <AgreementStep
                          setStep={setStep}
                          exisitngAccount={existingAccountResp || undefined}
                          captcha={{ verifyAsync: verifyAsync, verificationResp: verificationResp }}
                        />
                      )}
                      {/* {step === 1 && (
                      <SignupAgreementStep
                        exisitngAccount={existingAccountResp || undefined}
                        setStep={setStep}
                      />
                    )} */}
                    </Form>
                  </>
                </Formik>
              )}

              {settings && !settings.sign_up_enabled && (
                <div>
                  <h3>
                    <HelperWrapper t="register-disabled-label-1" />
                  </h3>
                  <p>
                    <HelperWrapper t="register-disabled-label-2" />
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button variant="outlined" onClick={() => window?.Intercom('show')}>
                      <HelperWrapper t="register-disabled-button" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          </Grid>
        </Grid>
      </AuthFormWrapper>
      <footer className={classes.footerWrapper}>
        <AppVersion />
      </footer>
      <Recaptcha captchaRef={captchaRef} />
    </Grid>
  )
}

export { Signup }
